import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Save, Building2, Utensils, Music, ShoppingBag, CalendarDays, Sparkles, Car, Hotel,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICE_LEVELS = [
  { value: 'economy', label: 'Economy / Limited Service' },
  { value: 'select', label: 'Select Service / Mid-Scale' },
  { value: 'full', label: 'Full Service' },
  { value: 'luxury', label: 'Luxury / Resort' },
];

const PROPERTY_TYPES = [
  { value: 'hotel', label: 'Hotel / Lodging', icon: Hotel },
  { value: 'restaurant', label: 'Standalone Restaurant / Bar', icon: Utensils },
  { value: 'entertainment', label: 'Entertainment / Recreation', icon: Music },
  { value: 'retail', label: 'Standalone Retail', icon: ShoppingBag },
];

const ANCILLARY_OPS = [
  { value: 'fb', label: 'Food & Beverage Outlets', icon: Utensils },
  { value: 'meetings', label: 'Meeting & Event Space', icon: CalendarDays },
  { value: 'spa', label: 'Spa & Wellness', icon: Sparkles },
  { value: 'retail', label: 'Retail Outlets', icon: ShoppingBag },
  { value: 'entertainment', label: 'Entertainment / Recreation', icon: Music },
  { value: 'valet', label: 'Valet / Parking Operations', icon: Car },
];

const FB_STYLES = [
  { value: 'fine_dining', label: 'Fine Dining' },
  { value: 'casual', label: 'Casual Dining' },
  { value: 'quick_service', label: 'Quick Service / Counter' },
  { value: 'room_service', label: 'Room Service / In-Room Dining' },
  { value: 'grab_go', label: 'Grab & Go / Market' },
];

export default function PropertyProfile({ propertyId, token, role, onClose }) {
  const [attrs, setAttrs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/properties/${propertyId}/attributes`, { headers })
      .then(res => {
        setAttrs(res.data.attributes || {});
        setPropertyName(res.data.property_name || '');
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, [propertyId, headers]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/properties/${propertyId}/attributes`, attrs, { headers });
      toast.success('Property profile saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setAttrs(prev => ({ ...prev, [key]: val }));
  const toggleAncillary = (op) => {
    const current = attrs.ancillary_operations || [];
    set('ancillary_operations', current.includes(op) ? current.filter(o => o !== op) : [...current, op]);
  };
  const toggleFbStyle = (style) => {
    const current = attrs.fb_service_styles || [];
    set('fb_service_styles', current.includes(style) ? current.filter(s => s !== style) : [...current, style]);
  };

  const propType = attrs.property_type || 'hotel';
  const serviceLevel = attrs.service_level || '';
  const ancillary = attrs.ancillary_operations || [];
  const isHotel = propType === 'hotel';
  const showFb = propType === 'restaurant' || ancillary.includes('fb');
  const showEntertainment = propType === 'entertainment' || ancillary.includes('entertainment');
  const showRetail = propType === 'retail' || ancillary.includes('retail');
  const showMeetings = ancillary.includes('meetings') || serviceLevel === 'full' || serviceLevel === 'luxury';
  const showSpa = ancillary.includes('spa') || serviceLevel === 'luxury';
  const showValet = ancillary.includes('valet');

  if (loading) return <div className="p-8 text-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-3xl mx-auto space-y-8" data-testid="property-profile">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{propertyName}</h2>
            <p className="text-sm text-muted-foreground">Property Profile & Attributes</p>
          </div>
          {isAdmin && (
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="save-profile">
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          )}
        </div>

        {/* Section 1: Core Classification */}
        <Section title="Core Classification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Property Type</Label>
              <Select value={propType} onValueChange={v => set('property_type', v)} disabled={!isAdmin}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isHotel && (
              <div>
                <Label className="text-xs">Service Level</Label>
                <Select value={serviceLevel} onValueChange={v => set('service_level', v)} disabled={!isAdmin}>
                  <SelectTrigger><SelectValue placeholder="Select service level" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {isHotel && (
            <div className="mt-4">
              <Label className="text-xs mb-2 block">Ancillary Operations (select all that apply)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ANCILLARY_OPS.map(op => {
                  const Icon = op.icon;
                  const checked = ancillary.includes(op.value);
                  return (
                    <label key={op.value} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-primary/10 border-primary/30' : 'hover:bg-muted/40'}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleAncillary(op.value)} disabled={!isAdmin} />
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs">{op.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </Section>

        {/* Section 2: Hotel Core Operations */}
        {isHotel && (
          <Section title="Hotel Core Operations">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="Total Guestrooms" value={attrs.total_guestrooms} onChange={v => set('total_guestrooms', v)} disabled={!isAdmin} hint="Drives PMS/CRS/RMS pricing (PRPM)" />
              <NumField label="Buildings / Towers" value={attrs.number_of_buildings} onChange={v => set('number_of_buildings', v)} disabled={!isAdmin} />
              <NumField label="Property Sq. Ft." value={attrs.total_property_sqft} onChange={v => set('total_property_sqft', v)} disabled={!isAdmin} />
              <NumField label="Front Desk Workstations" value={attrs.front_desk_workstations} onChange={v => set('front_desk_workstations', v)} disabled={!isAdmin} />
              <NumField label="Annual Reservations" value={attrs.annual_reservations} onChange={v => set('annual_reservations', v)} disabled={!isAdmin} hint="Drives payment gateway pricing" />
              <NumField label="Housekeeping Staff" value={attrs.housekeeping_staff} onChange={v => set('housekeeping_staff', v)} disabled={!isAdmin} hint="Drives CMMS per-user pricing" />
            </div>
            {(serviceLevel === 'full' || serviceLevel === 'luxury') && (
              <div className="flex gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <Switch checked={attrs.offers_bell_desk || false} onCheckedChange={v => set('offers_bell_desk', v)} disabled={!isAdmin} />
                  <Label className="text-xs">Bell Desk / Luggage Service</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={attrs.offers_digital_key || false} onCheckedChange={v => set('offers_digital_key', v)} disabled={!isAdmin} />
                  <Label className="text-xs">Digital Key / Mobile Check-in</Label>
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Section 3: F&B */}
        {showFb && (
          <Section title="Food & Beverage Operations">
            <div className="mb-4">
              <Label className="text-xs mb-2 block">Service Styles</Label>
              <div className="flex flex-wrap gap-2">
                {FB_STYLES.map(s => {
                  const checked = (attrs.fb_service_styles || []).includes(s.value);
                  return (
                    <label key={s.value} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer ${checked ? 'bg-primary/10 border-primary/30' : 'hover:bg-muted/40'}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleFbStyle(s.value)} disabled={!isAdmin} />
                      {s.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="F&B Outlets" value={attrs.fb_outlet_count} onChange={v => set('fb_outlet_count', v)} disabled={!isAdmin} hint="Per-location base fee" />
              <NumField label="Total Seats" value={attrs.fb_total_seats} onChange={v => set('fb_total_seats', v)} disabled={!isAdmin} />
              <NumField label="POS Terminals" value={attrs.fb_pos_terminals} onChange={v => set('fb_pos_terminals', v)} disabled={!isAdmin} hint="Per-terminal SaaS pricing" />
              <NumField label="Handheld Devices" value={attrs.fb_handheld_devices} onChange={v => set('fb_handheld_devices', v)} disabled={!isAdmin} />
              <NumField label="Kitchen Stations" value={attrs.fb_kitchen_stations} onChange={v => set('fb_kitchen_stations', v)} disabled={!isAdmin} hint="Drives KDS screen count" />
            </div>
          </Section>
        )}

        {/* Section 4: Entertainment */}
        {showEntertainment && (
          <Section title="Entertainment & Recreation">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label className="text-xs">Type</Label>
                <Select value={attrs.entertainment_type || ''} onValueChange={v => set('entertainment_type', v)} disabled={!isAdmin}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {['live_music', 'golf', 'marina', 'fec_arcade', 'bowling'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <NumField label="Venue Capacity" value={attrs.entertainment_capacity} onChange={v => set('entertainment_capacity', v)} disabled={!isAdmin} />
              <NumField label="Access/Scan Points" value={attrs.entertainment_access_points} onChange={v => set('entertainment_access_points', v)} disabled={!isAdmin} />
              <NumField label="Lanes / Bays / Sims" value={attrs.entertainment_lanes_bays} onChange={v => set('entertainment_lanes_bays', v)} disabled={!isAdmin} />
            </div>
          </Section>
        )}

        {/* Section 5: Retail */}
        {showRetail && (
          <Section title="Retail Operations">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="Retail Outlets" value={attrs.retail_outlet_count} onChange={v => set('retail_outlet_count', v)} disabled={!isAdmin} />
              <NumField label="Checkout / POS Terminals" value={attrs.retail_pos_terminals} onChange={v => set('retail_pos_terminals', v)} disabled={!isAdmin} />
              <NumField label="Estimated SKU Count" value={attrs.retail_sku_count} onChange={v => set('retail_sku_count', v)} disabled={!isAdmin} />
            </div>
          </Section>
        )}

        {/* Section 6: Meetings */}
        {showMeetings && (
          <Section title="Meeting & Event Space">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="Event Space Sq. Ft." value={attrs.event_space_sqft} onChange={v => set('event_space_sqft', v)} disabled={!isAdmin} hint="Drives diagramming pricing" />
              <NumField label="Meeting Rooms" value={attrs.meeting_room_count} onChange={v => set('meeting_room_count', v)} disabled={!isAdmin} />
              <NumField label="Sales & Catering Staff" value={attrs.sales_catering_staff} onChange={v => set('sales_catering_staff', v)} disabled={!isAdmin} hint="Per-user S&C pricing" />
            </div>
          </Section>
        )}

        {/* Section 7: Spa */}
        {showSpa && (
          <Section title="Spa & Wellness">
            <div className="grid grid-cols-2 gap-4">
              <NumField label="Treatment Rooms" value={attrs.spa_treatment_rooms} onChange={v => set('spa_treatment_rooms', v)} disabled={!isAdmin} hint="Primary spa software pricing" />
              <NumField label="Therapists / Providers" value={attrs.spa_providers} onChange={v => set('spa_providers', v)} disabled={!isAdmin} />
            </div>
          </Section>
        )}

        {/* Section 8: Valet */}
        {showValet && (
          <Section title="Valet & Parking">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="Parking Spaces" value={attrs.parking_spaces} onChange={v => set('parking_spaces', v)} disabled={!isAdmin} />
              <NumField label="Valet Zones" value={attrs.valet_zones} onChange={v => set('valet_zones', v)} disabled={!isAdmin} />
              <NumField label="Daily Parked Vehicles" value={attrs.daily_parked_vehicles} onChange={v => set('daily_parked_vehicles', v)} disabled={!isAdmin} />
            </div>
          </Section>
        )}
      </div>
    </ScrollArea>
  );
}

function Section({ title, children }) {
  return (
    <div className="border rounded-xl p-5 bg-card/50">
      <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{title}</h3>
      {children}
    </div>
  );
}

function NumField({ label, value, onChange, disabled, hint }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        min="0"
        value={value || ''}
        onChange={e => onChange(parseInt(e.target.value) || 0)}
        disabled={disabled}
        className="font-mono"
      />
      {hint && <p className="text-[9px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
